/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Lock, TrendingUp, MapPin, Cloud, Package, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmartRecommendations() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/recommendations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="glass-card p-6 mb-8 animate-pulse" data-testid="recs-skeleton">
        <div className="h-5 w-44 bg-[#1f221f] rounded mb-3" />
        <div className="h-7 w-56 bg-[#1f221f] rounded mb-4" />
        <div className="h-24 bg-[#141714] rounded" />
      </div>
    );
  }
  if (!data) return null;

  const { is_pro, teaser, top_baits, top_lakes, top_weather, suggested_bundle, personal } = data;
  // Soft-launch: bait commerce isn't public yet, so never surface bundle
  // upsells to anglers. Admins still see them for QA.
  const showBundles = !!user?.is_admin;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 mb-8 relative overflow-hidden"
      data-testid="smart-recs-section"
    >
      <div className="absolute -top-12 -left-12 w-40 h-40 bg-[#22c55e]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-[#22c55e]" />
          <div>
            <p className="text-xs uppercase tracking-wider text-[#22c55e] font-bold">Smart Recommendations</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-extrabold uppercase tracking-wide text-white">
              What's working right now
            </h2>
          </div>
        </div>
        <span className="text-xs text-[#6b7d6b] uppercase tracking-wider">Last 30 days</span>
      </div>

      {!is_pro ? (
        // Basic teaser
        <div className="p-4 bg-[#141714] rounded-lg" data-testid="recs-teaser">
          <div className="flex items-start gap-3 mb-4">
            <Lock className="w-5 h-5 text-[#c8b273] mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-white font-medium mb-1">Top bait this month: <span className="text-[#22c55e]">{teaser?.top_bait || 'Be the first to log!'}</span></p>
              <p className="text-[#8a948a] text-sm leading-snug">{teaser?.message}</p>
            </div>
          </div>
          {showBundles && teaser?.suggested_bundle && (
            <div className="flex items-center gap-3 p-3 bg-[#0b0d0b] rounded-lg border border-[#c8b273]/30">
              <Package className="w-8 h-8 text-[#c8b273] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold">Suggested bundle <span className="ml-1 px-1 py-0.5 text-[8px] tracking-widest border border-[#c8b273]/40 rounded">Admin</span></p>
                <p className="text-white text-sm font-bold truncate">{teaser.suggested_bundle.name}</p>
                <p className="text-[#6b7d6b] text-xs">£{teaser.suggested_bundle.price}</p>
              </div>
              <Link to="/shop">
                <Button size="sm" className="btn-gold whitespace-nowrap" data-testid="recs-shop-cta">
                  View <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          )}
          <Link to="/subscription">
            <Button className="btn-gold w-full mt-3" data-testid="recs-upgrade-cta">
              <Sparkles className="w-4 h-4 mr-1" /> Upgrade to Pro for full insights
            </Button>
          </Link>
        </div>
      ) : (
        // Pro view: full breakdown
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <RecsList
            title="Top baits"
            icon={TrendingUp}
            color="text-[#22c55e]"
            items={top_baits?.slice(0, 3).map((b) => ({ label: b.bait, value: `${b.catch_count} catches · best ${b.biggest_fish}lb` })) || []}
            testid="top-baits"
          />
          <RecsList
            title="Hottest lakes"
            icon={MapPin}
            color="text-[#c8b273]"
            items={top_lakes?.slice(0, 3).map((l) => ({ label: l.lake, value: `${l.catch_count} catches · best ${l.biggest_fish}lb` })) || []}
            testid="top-lakes"
          />
          <RecsList
            title="Best conditions"
            icon={Cloud}
            color="text-[#ff5e00]"
            items={top_weather?.slice(0, 3).map((w) => ({ label: w.weather, value: `${w.catch_count} catches · avg ${w.avg_weight}lb` })) || []}
            testid="top-weather"
          />
          {showBundles && suggested_bundle && (
            <div className="md:col-span-2 lg:col-span-3 p-4 bg-[#0b0d0b] rounded-lg border border-[#c8b273]/30 flex items-center gap-4" data-testid="recs-bundle">
              <Package className="w-10 h-10 text-[#c8b273] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold">Suggested for these conditions <span className="ml-1 px-1 py-0.5 text-[8px] tracking-widest border border-[#c8b273]/40 rounded">Admin</span></p>
                <p className="text-white text-base font-bold">{suggested_bundle.name}</p>
                <p className="text-[#8a948a] text-xs">{suggested_bundle.reason} · £{suggested_bundle.price}</p>
              </div>
              <Link to="/shop">
                <Button className="btn-gold whitespace-nowrap" data-testid="recs-bundle-cta">
                  Get this bundle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          )}
          {personal && (
            <div className="md:col-span-2 lg:col-span-3 grid grid-cols-3 gap-3" data-testid="recs-personal">
              <Stat label="Your catches" value={personal.catch_count || 0} />
              <Stat label="Your total weight" value={`${personal.total_weight || 0}lb`} />
              <Stat label="Your biggest" value={`${personal.biggest_fish || 0}lb`} />
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

const RecsList = ({ title, icon: Icon, color, items, testid }) => (
  <div className="p-3 bg-[#141714] rounded-lg" data-testid={`recs-${testid}`}>
    <p className="text-xs uppercase tracking-wider text-[#8a948a] font-bold mb-2 flex items-center gap-1">
      <Icon className={`w-3.5 h-3.5 ${color}`} /> {title}
    </p>
    {items.length === 0 ? (
      <p className="text-[#6b7d6b] text-xs">No data yet this month</p>
    ) : (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex flex-col">
            <span className="text-white text-sm font-medium truncate">{it.label}</span>
            <span className="text-[#6b7d6b] text-xs">{it.value}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="p-3 bg-[#141714] rounded-lg text-center">
    <p className="text-[10px] uppercase tracking-wider text-[#6b7d6b] mb-1">{label}</p>
    <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#c8b273]">{value}</p>
  </div>
);
